import React, { createContext, useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { toast } from 'day8-ui';
import moment from 'moment';
import Konami from 'react-konami';
import PropTypes from 'prop-types';
import { cancelBooking, getActiveBooking } from '../actions/booking';
import { useDispatch, useSelector } from 'react-redux';
import { BOOKING, CHECKOUT_TIMER_IN_MINUTES, PAYMENT } from '../constants';
import history from '../history';

export const initialValues = {
  timeoutDateTime: undefined,
  currentBooking: undefined,
  timeoutBooking: () => {},
  isTimingOut: undefined,
  checkPreviousBooking: () => {},
  shouldShowCheckout: undefined
};
export const BookingBasketContext = createContext(initialValues);

export const BookingBasketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const currentBooking = useSelector(state => state.booking.currentBooking);
  const [timeoutDateTime, setTimeoutDateTime] = useState(null);
  const [isTimingOut, setIsTimingOut] = useState(false);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const clearTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      setTimeoutDateTime(null);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // This is useful for checking on payment page if booking has been replaced
  const checkPreviousBooking = useCallback(async bookingId => {
    try {
      // 1. fetch active booking
      const activeBooking = await dispatch(getActiveBooking());
      // 2. compare to previous booking (if exists)
      if (activeBooking && activeBooking.id !== bookingId) {
        toast.warning('Reservation updated!');
      }
      if (!activeBooking) {
        clearTimeouts();
      }
    } catch (e) {
      console.error(e);
      clearTimeouts();
    }
  });

  const timeoutBooking = useCallback(async () => {
    try {
      // clearTimeouts();
      // 1. check fresh active booking
      const prevBooking = currentBooking;
      const activeBooking = await dispatch(getActiveBooking());
      // this means that in the meantime booking has changed... do nothing - the fetch for active booking will change state and trigger re-render of everything with new timer
      if (prevBooking?.id !== activeBooking?.id) {
        return;
      }
      if (currentBooking && currentBooking.status === BOOKING.PENDING_STATUS && !currentBooking.mainOrder.payments.some(p => [PAYMENT.PAID_STATUS, PAYMENT.PENDING_STATUS].includes(p.status))) {
        setIsTimingOut(true);
        toast.info('Your reservation has expired. Cancelling...');
        await dispatch(cancelBooking(currentBooking.id));
        toast.success('Reservation cancelled!');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsTimingOut(false);
    }
  }, [currentBooking]);

  const startTimeout = () => {
    // Clear any existing timeout
    clearTimeouts();
    const now = moment.utc(new Date());
    const timeoutDateTime = moment.utc(currentBooking.bookingDate).add(CHECKOUT_TIMER_IN_MINUTES, 'minutes');
    const duration = moment.duration(timeoutDateTime.diff(now)).asMilliseconds();
    // Create a new timeout and store it in ref
    timeoutRef.current = setTimeout(timeoutBooking, duration);
    setTimeoutDateTime(timeoutDateTime.toISOString());

    intervalRef.current = setInterval(() => {
      if (currentBooking?.id && currentBooking.status === BOOKING.PENDING_STATUS) {
        checkPreviousBooking(currentBooking.id);
      }
    }, 10000); // Poll every 10 seconds
  };

  useEffect(() => {
    // ignore when in /account* pages
    // When going to /account* pages, we don't want to fetch active booking because OK booking page is stored in same currentBooking store
    if (!history.location.pathname.startsWith('/account')) {
      // on first load fetch active booking
      dispatch(getActiveBooking());
    }
  }, []);

  useEffect(() => {
    if (currentBooking?.id && currentBooking.status === BOOKING.PENDING_STATUS) {
      startTimeout();
    }
    if (currentBooking?.id && currentBooking.status === BOOKING.OK_STATUS) {
      clearTimeouts();
    }
  }, [currentBooking?.id, currentBooking?.status]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, []);

  // This should be useful as check when click BookButton again from funnel
  // const shouldOpenWarning = useCallback(async (standardYachtId, yachtId, cabinWeekIds) => {
  //   try {

  //   } catch (e) {
  //     console.error(e);
  //   }
  // }, []);

  const contextValue = useMemo(() => ({ timeoutDateTime, currentBooking, timeoutBooking, isTimingOut, checkPreviousBooking, shouldShowCheckout: timeoutDateTime && currentBooking }), [timeoutDateTime, currentBooking, timeoutBooking, isTimingOut, checkPreviousBooking]);

  return (
    <BookingBasketContext.Provider value={contextValue}>
      <Konami
        easterEgg={timeoutBooking}
        konami={[65, 66, 65, 78, 68, 79, 78, 77, 69]}
      />
      {children}
    </BookingBasketContext.Provider>
  );
};

BookingBasketProvider.propTypes = {
  children: PropTypes.any
};

export const useBookingBasket = () => useContext(BookingBasketContext);

export const withBookingBasket = WrappedComponent => {
  return function BookingBasketComponent(props) {
    return (
      <BookingBasketContext.Consumer>
        {context => <WrappedComponent {...props} bookingBasketContext={context} />}
      </BookingBasketContext.Consumer>
    );
  };
};